import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useDashboard } from '@/api/queries/useDashboard';
import { Page, PageContent, Skeleton } from '@bliks-io/ui';
import { QueryErrorRetry } from '@/components/query-error-retry/QueryErrorRetry';
import { DashboardStatCards } from './DashboardStatCards';
import { DashboardProcessList } from './DashboardProcessList';
import { DashboardSystemsTable } from './DashboardSystemsTable';
import { DashboardCoWorkers } from './DashboardCoWorkers';
import { DashboardCapacityChart } from './DashboardCapacityChart';

export function DashboardPage() {
  const { t } = useTranslation();
  useDocumentTitle('pageTitles.dashboard');

  const { data, isLoading, isError, isFetching, refetch } = useDashboard();

  const header = (
    <div>
      <h1 className="text-2xl font-bold">{t('dashboard.title')}</h1>
      <p className="text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
    </div>
  );

  if (isLoading) {
    return (
      <Page>
        <PageContent>
          <div className="space-y-6">
            {header}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-33" />
              ))}
            </div>
            <Skeleton className="h-103" />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Skeleton className="h-75" />
              <Skeleton className="h-75" />
            </div>
            <Skeleton className="h-70" />
          </div>
        </PageContent>
      </Page>
    );
  }

  if (isError || !data) {
    return (
      <Page>
        <PageContent>
          <QueryErrorRetry onRetry={refetch} loading={isFetching} className="min-h-[400px]" />
        </PageContent>
      </Page>
    );
  }

  return (
    <Page>
      <PageContent>
        <div className="space-y-6">
          {header}

          <DashboardStatCards stats={data.stats} />

          <DashboardCapacityChart processes={data.processes} />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <DashboardProcessList processes={data.processes} />
            <DashboardSystemsTable systems={data.systems} />
          </div>

          <DashboardCoWorkers coWorkers={data.coWorkers} />
        </div>
      </PageContent>
    </Page>
  );
}
